import { Button } from 'ui/Button';
import styles from './ReportGenerationAlert.module.scss';
import { ReactComponent as IconAct } from './img/iconAct.svg';
import { ReactComponent as IconDowload } from './img/iconDownload.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { getAuthorizationHeader } from 'redux/Api/employedsApi/employedsApi';
import { useState, useEffect, useRef } from 'react';

const BASE_STORAGE_KEY = 'reportGenerationAlert';
const PERSISTENCE_DURATION = 3 * 24 * 60 * 60 * 1000;
const POLLING_INTERVAL = 10000;

interface AlertData {
  fileId: string;
  status: 'loading' | 'ready';
  timestamp: number;
  createdAt: string;
  type: 'report' | 'documents';
  ownerCustomerId?: number;
  ownerUserId?: number;
}

export const ReportGenerationAlert = ({
  fileId,
  status,
  onClose,
  onCancel,
  onDownloadSuccess,
  onDownloadError,
  onFileFailed,
  userId,
  customerId,
}: {
  fileId?: string;
  status?: 'loading' | 'ready';
  onClose: () => void;
  onCancel: () => void;
  onDownloadSuccess?: () => void;
  onDownloadError?: () => void;
  onFileFailed?: (message?: string) => void;
  userId: number | null;
  customerId: number | undefined;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const canScopeStorage =
    customerId !== undefined && userId !== null && userId !== undefined;

  const storedAlertBelongsToCurrentUser = (data: AlertData): boolean => {
    if (!canScopeStorage) return false;
    if (data.ownerCustomerId !== undefined || data.ownerUserId !== undefined) {
      return data.ownerCustomerId === customerId && data.ownerUserId === userId;
    }
    return true;
  };

  const fetchFileStatus = async (
    fileId: string,
  ): Promise<{ status: 'loading' | 'ready' | 'failed'; message?: string }> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/orders/docs/status/${fileId}`,
        {
          method: 'GET',
          headers: {
            Authorization: getAuthorizationHeader(),
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        const apiStatus = result.status;
        const message = result.message;

        switch (apiStatus) {
          case 'completed':
            return { status: 'ready' };
          case 'failed':
          case 'cancelled':
            return { status: 'failed', message };
          case 'draft':
          case 'processing':
          default:
            return { status: 'loading' };
        }
      } else {
        console.error('Ошибка при получении статуса файла');
        return { status: 'loading' };
      }
    } catch (error) {
      console.error('Ошибка при получении статуса файла', error);
      return { status: 'loading' };
    }
  };

  const startPolling = (fileId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      const currentStatus = await fetchFileStatus(fileId);

      if (currentStatus.status === 'failed') {
        stopPolling();
        onFileFailed?.(currentStatus.message);
        clearAlert();
        return;
      }

      if (currentStatus.status === 'ready') {
        stopPolling();
      }

      setAlertData((prev) => {
        if (
          prev &&
          (currentStatus.status === 'loading' || currentStatus.status === 'ready')
        ) {
          const updatedData = {
            ...prev,
            status: currentStatus.status as 'loading' | 'ready',
          };
          const storageKey = getStorageKey(prev.fileId);
          if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(updatedData));
          }
          return updatedData;
        }
        return prev;
      });
    }, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const getStorageKey = (fId?: string): string | null => {
    if (!canScopeStorage) return null;

    if (fId) {
      const reportKey = `${BASE_STORAGE_KEY}_report_${customerId}_${userId}`;
      const documentsKey = `${BASE_STORAGE_KEY}_documents_${customerId}_${userId}`;

      const reportData = localStorage.getItem(reportKey);
      const documentsData = localStorage.getItem(documentsKey);

      if (reportData) {
        try {
          const data = JSON.parse(reportData) as AlertData;
          if (data.fileId === fId && storedAlertBelongsToCurrentUser(data))
            return reportKey;
        } catch (e) {
          console.error('Ошибка при парсинге данных', e);
        }
      }

      if (documentsData) {
        try {
          const data = JSON.parse(documentsData) as AlertData;
          if (data.fileId === fId && storedAlertBelongsToCurrentUser(data))
            return documentsKey;
        } catch (e) {
          console.error('Ошибка при парсинге данных', e);
        }
      }

      return null;
    }

    const reportKey = `${BASE_STORAGE_KEY}_report_${customerId}_${userId}`;
    const documentsKey = `${BASE_STORAGE_KEY}_documents_${customerId}_${userId}`;

    const rawReport = localStorage.getItem(reportKey);
    if (rawReport) {
      try {
        const data = JSON.parse(rawReport) as AlertData;
        if (storedAlertBelongsToCurrentUser(data)) return reportKey;
      } catch {
        //
      }
    }
    const rawDocuments = localStorage.getItem(documentsKey);
    if (rawDocuments) {
      try {
        const data = JSON.parse(rawDocuments) as AlertData;
        if (storedAlertBelongsToCurrentUser(data)) return documentsKey;
      } catch {
        //
      }
    }

    return null;
  };

  useEffect(() => {
    if (fileId) {
      stopPolling();

      if (canScopeStorage) {
        localStorage.removeItem(`${BASE_STORAGE_KEY}_report_${customerId}_${userId}`);
        localStorage.removeItem(`${BASE_STORAGE_KEY}_documents_${customerId}_${userId}`);

        const newStorageKey = `${BASE_STORAGE_KEY}_documents_${customerId}_${userId}`;
        const newData: AlertData = {
          fileId,
          status: status || 'loading',
          timestamp: Date.now(),
          createdAt: new Date().toISOString(),
          type: 'documents',
          ownerCustomerId: customerId,
          ownerUserId: userId,
        };
        localStorage.setItem(newStorageKey, JSON.stringify(newData));
        setAlertData(newData);
      } else {
        setAlertData({
          fileId,
          status: status || 'loading',
          timestamp: Date.now(),
          createdAt: new Date().toISOString(),
          type: 'documents',
        });
      }
      setShouldShow(true);

      if ((status || 'loading') === 'loading') {
        startPolling(fileId);
      }

      return;
    }

    if (!canScopeStorage) {
      return;
    }

    const storageKey = getStorageKey();
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const data: AlertData = JSON.parse(stored);
          if (!storedAlertBelongsToCurrentUser(data)) {
            localStorage.removeItem(storageKey);
            return;
          }
          const now = Date.now();
          const elapsed = now - data.timestamp;

          if (elapsed < PERSISTENCE_DURATION) {
            fetchFileStatus(data.fileId).then((actualStatus) => {
              if (actualStatus.status === 'failed') {
                onFileFailed?.(actualStatus.message);
                localStorage.removeItem(storageKey);
                return;
              }

              const updatedData = {
                ...data,
                status: actualStatus.status as 'loading' | 'ready',
              };
              localStorage.setItem(storageKey, JSON.stringify(updatedData));
              setAlertData(updatedData);
              setShouldShow(true);

              if (actualStatus.status === 'loading') {
                startPolling(data.fileId);
              }
            });
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch (error) {
          console.error('Ошибка при парсинге данных', error);
          localStorage.removeItem(storageKey);
        }
      }
    }
  }, [fileId, status, customerId, userId]);

  useEffect(() => {
    if (
      alertData &&
      fileId &&
      status === 'ready' &&
      alertData.fileId === fileId &&
      alertData.status !== 'ready'
    ) {
      const storageKey = getStorageKey(fileId);
      const updatedData = { ...alertData, status: 'ready' as const };
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(updatedData));
      }
      setAlertData(updatedData);
      stopPolling();
    }
  }, [status, alertData, fileId, customerId, userId]);

  const clearAlert = () => {
    stopPolling();
    if (alertData && canScopeStorage) {
      let storageKey = getStorageKey(alertData.fileId);
      if (!storageKey) {
        const reportKey = `${BASE_STORAGE_KEY}_report_${customerId}_${userId}`;
        const documentsKey = `${BASE_STORAGE_KEY}_documents_${customerId}_${userId}`;
        for (const key of [reportKey, documentsKey]) {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw) as AlertData;
            if (
              parsed.fileId === alertData.fileId &&
              storedAlertBelongsToCurrentUser(parsed)
            ) {
              storageKey = key;
              break;
            }
          } catch {
            //
          }
        }
      }
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    }
    setShouldShow(false);
    setAlertData(null);
  };

  if (!shouldShow || !alertData) return null;

  const getFormattedDateTime = () => {
    const alertDate = new Date(alertData.createdAt);
    const months = [
      'января',
      'февраля',
      'марта',
      'апреля',
      'мая',
      'июня',
      'июля',
      'августа',
      'сентября',
      'октября',
      'ноября',
      'декабря',
    ];

    const day = alertDate.getDate();
    const month = months[alertDate.getMonth()];
    const hours = alertDate.getHours().toString().padStart(2, '0');
    const minutes = alertDate.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} в ${hours}:${minutes}`;
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const responseToken = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customer/docs/download/one-time-token`,
        {
          method: 'GET',
          headers: {
            Authorization: getAuthorizationHeader(),
          },
        },
      );

      const { token } = await responseToken.json();

      const url = `${process.env.REACT_APP_API_URL}/api/customer/docs/report/${alertData.fileId}/download/${token}`;
      window.open(url, '_self');

      onDownloadSuccess?.();
      clearAlert();
      onClose();
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      onDownloadError?.();
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancel = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/orders/docs/cancel/${alertData.fileId}`,
        {
          method: 'POST',
          headers: {
            Authorization: getAuthorizationHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file_id: parseInt(alertData.fileId),
          }),
        },
      );

      if (response.ok) {
        onCancel();
        clearAlert();
        onClose();
      } else {
        console.error('Не удалось отменить генерацию файла');
      }
    } catch (error) {
      console.error('Ошибка при отмене генерации файла:', error);
    }
  };

  const handleClose = () => {
    clearAlert();
    onClose();
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.header_left}>
          <IconAct style={{ height: '36px', width: '36px' }} />
          <div className={styles.header_text}>
            <span className={styles.header_title}>Скачать документы</span>
            <div className={styles.body}>
              <span>
                {alertData.status === 'loading'
                  ? 'Подготовка может занять некоторое время'
                  : `Документы сформированы ${getFormattedDateTime()}`}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.header_right}>
          {alertData.status === 'loading' ? (
            <Button theme='outline' className={styles.btn} loading>
              Формируем документы
            </Button>
          ) : (
            <Button
              theme='outline'
              className={styles.btn}
              onClick={handleDownload}
              loading={isDownloading}>
              <div className={styles.btn_text}>
                Скачать {!isDownloading && <IconDowload />}
              </div>
            </Button>
          )}
          <IconClose
            style={{ cursor: 'pointer' }}
            onClick={alertData.status === 'loading' ? handleCancel : handleClose}
          />
        </div>
      </div>
    </div>
  );
};
